import { IGridImage } from "api/interfaces/grid-image.type";
import VizSensor from "component/design-system/VizSensor";
import { GridImages } from "component/grid-images";
import { useRouter } from "next/router";
import HideIf from "util/HideIf";
import { preformContentLinkClick } from "util/url/preformContentLinkClick";
import LeftRightImages from "./LeftRightImages";
import TopBottomItems from "./TopBottomItems";
import styles from "./article-item.module.scss";

interface IProps {
  title: string;
  imagesUrl: [];
  content: string;
  imgSrc: string;
  className: string;
  layout: "left" | "right" | "top" | "bottom";
  useVizSensor: any;
  gridImages: IGridImage;
  leftRightImages: Array<any>;
  imagesUrlV2?: {
    imagesUrl: string;
    url: string;
    screen: string;
    height: number;
  }[];
  hidden?: boolean;
  headingType: string;

}
function ArticleItem(props: IProps) {
  const {
    leftRightImages, layout, imagesUrlV2, imagesUrl, imgSrc, useVizSensor, title,
    gridImages, className, headingType = "h3", hidden, content,
  } = props;

  const router = useRouter();

  function isImageV2Present() {
    if (!imagesUrlV2) return false;
    return true;
  }

  function gridImagesPresent() {
    if (!gridImages) {
      return false;
    }
    return true;
  }

  function renderTopBottomImages() {
    return (
      <div>
        <TopBottomItems
          imagesUrl={imagesUrl}
          imgSrc={imgSrc}
          useVizSensor={useVizSensor}
          imagesUrlV2={imagesUrlV2}
          title={title}
        />
      </div>
    );
  }

  function renderLeftLayout() {
    const isLeftRightImageInformationPresent = leftRightImages?.every((image: any) => image.imageUrl && image.url);
    const isImageUrlPresent = imagesUrl && imagesUrl.length !== 0;

    const canDisplayContent = isLeftRightImageInformationPresent || isImageUrlPresent;

    return (
      <div className={`media ${styles.left_layout}`}>
        <HideIf show={canDisplayContent}>
          <div className="media-top visible-xs">
            <LeftRightImages
              leftRightImages={leftRightImages}
              imagesUrl={imagesUrl}
              useVizSensor={useVizSensor}
            />
          </div>

          <div className="media-left hidden-xs m-r-10" style={{ minWidth: "calc(25 * var(--safari-root-font, 10px))" }}>
            <LeftRightImages
              leftRightImages={leftRightImages}
              imagesUrl={imagesUrl}
              useVizSensor={useVizSensor}
            />
          </div>
        </HideIf>
        {renderHtml()}
      </div>
    );
  }

  function handleContentClick(e: any) {
    preformContentLinkClick(router, e);
  }

  function renderHtml(_className = "") {
    const children = (
      <div
        onClick={handleContentClick}
        onKeyPress={handleContentClick}
        className={`${styles.article_item} ${_className}`}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
    if (useVizSensor) {
      return (
        <VizSensor bottom={50}>
          {children}
        </VizSensor>
      );
    }
    return children;
  }

  function renderRightLayout() {
    const isLeftRightImageInformationPresent = leftRightImages?.every((image: any) => image.imageUrl && image.url);
    const isImageUrlPresent = imagesUrl && imagesUrl.length !== 0;

    const canDisplayContent = isLeftRightImageInformationPresent || isImageUrlPresent;
    // https://partsavatar.ca/diy-auto-repair-tools
    return (
      <div className={`media ${styles.right_layout}`}>
        {renderHtml(styles.text_left)}
        <HideIf show={canDisplayContent}>
          <div className="hidden-sm hidden-md hidden-lg">
            <div className="media-top">
              <LeftRightImages
                leftRightImages={leftRightImages}
                imagesUrl={imagesUrl}
                useVizSensor={useVizSensor}
              />
            </div>
          </div>
          <div className="hidden-xs">
            <div className="media-right" style={{ minWidth: "calc(25 * var(--safari-root-font, 10px))" }}>
              <LeftRightImages
                leftRightImages={leftRightImages}
                imagesUrl={imagesUrl}
                useVizSensor={useVizSensor}
              />
            </div>
          </div>
        </HideIf>
      </div>
    );
  }

  function renderTopLayout() {
    const isGridImagesPresent = gridImagesPresent();
    return (
      <div className="media">

        <HideIf show={(imagesUrl && imagesUrl.length > 0) || (isImageV2Present())}>
          <div className="media-top">{renderTopBottomImages()}</div>
        </HideIf>
        <HideIf show={isGridImagesPresent}>
          <GridImages
            gridImages={gridImages}
            imgSrc={imgSrc}
          />
        </HideIf>
        {renderHtml()}
      </div>
    );
  }

  function renderBottomLayout() {
    const isGridImagesPresent = gridImagesPresent();
    return (
      <div className="media">
        {renderHtml()}
        <HideIf show={isGridImagesPresent}>
          <GridImages
            gridImages={gridImages}
            imgSrc={imgSrc}
          />
        </HideIf>
        <HideIf show={(imagesUrl && imagesUrl.length > 0) || (isImageV2Present())}>

          <div className="media-bottom">{renderTopBottomImages()}</div>
        </HideIf>
      </div>
    );
  }

  function renderLayoutHandler() {
    let renderLayout;
    switch (layout) {
    case "right":
      renderLayout = renderRightLayout();
      break;
    case "top":
      renderLayout = renderTopLayout();
      break;
    case "bottom":
      renderLayout = renderBottomLayout();
      break;
    case "left":
    default:
      renderLayout = renderLeftLayout();
      break;
    }
    return renderLayout;
  }

  // hidden is added in OBD Pages.
  if (hidden) {
    return null;
  }
  return (
    <div className={`article-item ${className} ${styles.container}`}>

      {headingType === "h2" && title && <h2 id={title} className={styles.article_title_h2}>{title}</h2>}

      {headingType === "h3" && title && <h3 id={title} className={styles.article_title_h3}>{title}</h3>}
      {renderLayoutHandler()}

    </div>
  );
}

export default ArticleItem;
